import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import * as formHelpers from '../../../scripts/formHelpers';
import * as formValidators from '../../../scripts/formValidators';
import $ from 'jquery';
import {
  MotifButton,
  MotifModalBody,
  MotifModalFooter,
  MotifToast,
  MotifIcon,
  MotifErrorMessage,
} from '@ey-xd/motif-react';
import { IconoirInfoCircle } from '@ey-xd/motif-icon';
import { addAttribute } from '../../../common/Attribute';
import { useSelector } from 'react-redux';
import * as formParsers from '../../../scripts/formParsers';
import { DateField } from '../../shared/formFields';
// Helper function to format REIT display text
const getReitDisplayText = (reit) =>
  `${reit.reitShortName}_${reit.reitTrackingID}`;

const EditSecurityForm = ({
  handleSubmit,
  handleCloseForm,
  initialValues,
  securityFormPurpose,
  showSecuritySuccess,
  error,
  submitting,
  handleDismissErrorMessage,
  isTestingEnabled,
  onSubmit,
  fields,
  reits,
  validationMessage,
  clearValidationMessage,
}) => {
  const [acquisitionDateFutureError, setAcquisitionDateFutureError] =
    useState('');
  const [endDateValidationError, setendDateValidationError] = useState('');
  const selector = formValueSelector('security');
  const acquisitionDate = useSelector((state) =>
    selector(state, 'acquisitionDate')
  );
  const endDate = useSelector((state) => selector(state, 'endDate'));

  useEffect(() => {
    //Add Attribute(type=button) to all button elements having attribute(role = 'combobox')
    addAttribute("button[role = 'combobox']", 'type', 'button');
  });

  // Add validation between acquisition date and end date
  useEffect(() => {
    const validateDates = () => {
      if (acquisitionDate && endDate) {
        const acquisitionDateObj = new Date(acquisitionDate);
        const endDateObj = new Date(endDate);
        if (endDateObj < acquisitionDateObj) {
          setendDateValidationError(
            'The End Date cannot be earlier than the Acquisition Date.'
          );
        } else {
          setendDateValidationError('');
        }
      } else {
        setendDateValidationError('');
      }
    };

    validateDates();
  }, [acquisitionDate, endDate]);

  useEffect(() => {
    const validateAcquisitionDate = () => {
      if (acquisitionDate) {
        const acquisitionDateObj = new Date(acquisitionDate);
        const today = new Date();
        // Remove time portion for accurate date comparison
        today.setHours(0, 0, 0, 0);
        if (acquisitionDateObj > today) {
          setAcquisitionDateFutureError(
            'Security Acquisition Date cannot be in the future.'
          );
        } else {
          setAcquisitionDateFutureError('');
        }
      } else {
        setAcquisitionDateFutureError('');
      }
    };

    validateAcquisitionDate();
  }, [acquisitionDate]);

  let submitButtonText = 'Save';

  let scrollTop = () => {
    $('.ReactModal__Content').scrollTop(0);
  };

  let save = (e) => {
    e.preventDefault();
    if (endDateValidationError || acquisitionDateFutureError) {
      return; // Prevent form submission if validation errors exist
    }
    scrollTop();
    handleSubmit((values) => onSubmit({ ...values }))();
  };
  const reitOptions =
    securityFormPurpose === 'EDIT'
      ? reits.map((r) => ({
          key: r.reitid,
          value: r.reitid,
          text: getReitDisplayText(r),
        }))
      : reits
          .filter((r) => r.isActive === true)
          .map((r) => ({
            key: r.reitid,
            value: r.reitid,
            text: getReitDisplayText(r),
          }));
  return (
    <>
      <MotifModalBody style={{ paddingTop: 0 }}>
        <form
          onSubmit={handleSubmit}
          className="security"
          id="securityForm"
          style={{ display: 'flex' }}
        >
          <div style={{ width: '100%' }}>
            <Field
              name="securitiesMasterID"
              component={formHelpers.renderField}
              type="hidden"
            />
            <section className="security">
              {formHelpers.showSubmissionError(
                error,
                handleDismissErrorMessage
              )}

              {/* General validation message display - for any messages that don't match specific conditions */}
              {validationMessage &&
                !validationMessage.includes('Acquisition Date must be in') &&
                !validationMessage.includes('End Date must be in') &&
                !validationMessage.includes('End Date must be after') &&
                validationMessage !==
                  'Security Name, Issuer and Class already exist within the REIT.' && (
                  <MotifErrorMessage
                    variant="error"
                    data-testid="generalValidationError"
                  >
                    {validationMessage}
                  </MotifErrorMessage>
                )}

              <div className="row ">
                <div className="field col-md-12" style={{ minHeight: '44px' }}>
                  <Field
                    name="reitid"
                    label="REIT Name (Required)"
                    placeholder="Select a REIT"
                    component={formHelpers.SingleSelectComponent}
                    type="select"
                    options={reitOptions}
                    optionText={(r) => r.text}
                    optionValue={(r) => r.value}
                    isRequiredField={true}
                    validate={[
                      formValidators.required,
                      formValidators.minLength2,
                    ]}
                    testingData={{ label: 'lblREITName', div: 'divREITName' }}
                    disabled={securityFormPurpose === 'EDIT' ? true : false}
                  />
                </div>
              </div>
              <div className="row ">
                <div className="field col-md-12">
                  <Field
                    name="securityName"
                    component={formHelpers.renderField}
                    type="text"
                    label="Security Name"
                    isRequiredField={true}
                    maxLength={255}
                    validate={[
                      formValidators.required,
                      formValidators.maxLength255,
                      formValidators.noWindowSpecialChars,
                    ]}
                    data-testid="txtSecurityName"
                    onChange={clearValidationMessage}
                  />
                </div>
              </div>
              <div className="row">
                <div className="field col-md-6">
                  <DateField
                    name="acquisitionDate"
                    label="Acquisition Date"
                    placeholder="MM/DD/YYYY"
                    isRequiredField={true}
                    validate={[
                      initialValues && !initialValues.isSystemGenerated
                        ? formValidators.required
                        : null,
                      initialValues && !initialValues.isSystemGenerated
                        ? formValidators.isValidDate
                        : null,
                    ].filter(Boolean)}
                    dataTestId="txtacquisitionDate"
                    disabled={
                      initialValues && initialValues.isSystemGenerated
                        ? true
                        : false
                    }
                    onChange={clearValidationMessage}
                  />
                  {/* Display client-side validation error */}
                  {acquisitionDateFutureError && (
                    <MotifErrorMessage variant="error">
                      {acquisitionDateFutureError}
                    </MotifErrorMessage>
                  )}

                  {/* Display API validation messages */}
                  {validationMessage && (
                    <>
                      {validationMessage.includes(
                        'Acquisition Date must be in'
                      ) && (
                        <MotifErrorMessage variant="error">
                          {validationMessage}
                        </MotifErrorMessage>
                      )}
                    </>
                  )}
                </div>
                <div className="field col-md-6">
                  <DateField
                    name="endDate"
                    label="End Date"
                    validate={[formValidators.isValidDate]}
                    dataTestId="txtEndDate"
                    disabled={
                      initialValues && initialValues.isSystemGenerated
                        ? true
                        : false
                    }
                    onChange={clearValidationMessage}
                  />

                  {/* Display validation messages from API */}
                  {validationMessage && (
                    <>
                      {validationMessage.includes('End Date must be in') && (
                        <MotifErrorMessage variant="error">
                          {validationMessage}
                        </MotifErrorMessage>
                      )}

                      {validationMessage.includes('End Date must be after') && (
                        <MotifErrorMessage variant="error">
                          {validationMessage}
                        </MotifErrorMessage>
                      )}
                    </>
                  )}

                  {/* Display client-side validation error */}
                  {endDateValidationError && (
                    <div data-testid="endDateError">
                      <MotifErrorMessage
                        variant="error"
                        className="validation-error"
                      >
                        {endDateValidationError}
                      </MotifErrorMessage>
                    </div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="field col-md-6">
                  <Field
                    name="issuer"
                    component={formHelpers.renderField}
                    type="text"
                    label="Issuer"
                    maxLength={255}
                    isRequiredField={true}
                    validate={[
                      formValidators.required,
                      formValidators.maxLength255,
                    ]}
                    data-testid="txtIssuer"
                    onChange={clearValidationMessage}
                  />
                </div>
                <div className="field col-md-6">
                  <Field
                    name="class"
                    label="Class"
                    component={formHelpers.renderField}
                    type="text"
                    maxLength={255}
                    isRequiredField={true}
                    validate={[
                      formValidators.required,
                      formValidators.maxLength255,
                    ]}
                    data-testid="txtClass"
                    onChange={clearValidationMessage}
                  />
                </div>
              </div>
              {(validationMessage ===
                'Security Name, Issuer and Class already exist within the REIT.' && (
                <MotifErrorMessage variant="error">
                  {validationMessage}
                </MotifErrorMessage>
              )) ||
                null}
            </section>
          </div>
        </form>
      </MotifModalBody>
      <MotifModalFooter>
        <MotifButton
          variant="secondary"
          type="button"
          onClick={handleCloseForm}
          form="securityForm"
          data-testid="btnCancel"
        >
          Cancel
        </MotifButton>
        <MotifButton
          variant="primary"
          type="submit"
          onClick={save}
          disabled={submitting}
          form="securityForm"
          data-testid="btnSave"
        >
          {submitButtonText}
        </MotifButton>
      </MotifModalFooter>
    </>
  );
};

EditSecurityForm.propTypes = {
  lookupTypes: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  handleCloseForm: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  error: PropTypes.object,
  submitting: PropTypes.bool,
  securityFormPurpose: PropTypes.string,
  showSecuritySuccess: PropTypes.bool,
  handleDismissErrorMessage: PropTypes.func,
  isTestingEnabled: PropTypes.bool,
  fields: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  clientIsAudit: PropTypes.bool,
  change: PropTypes.func.isRequired,
  validationMessage: PropTypes.string,
};

export default reduxForm({ form: 'security' })(EditSecurityForm);
